<script setup lang="ts">
/**
 * Activity toast — a single non-blocking reward-feedback toast (M4C-29).
 *
 * Renders the Figma `activity / toast`: a white card with a 2px ring, soft
 * shadow and a lime-bordered (M4C-23 `onboarding-border`) inner row. The title
 * sits left; the trailing slot shows the reward — a cyan `+N` for fragment gains
 * (`fragments_gained`) or a gold star coin for star milestones (`milestone` /
 * `star_born`). Hex-free; all colour comes from design tokens.
 *
 * The toast owns its own lifetime: it auto-dismisses after `duration` ms (paused
 * while hovered/focused) and emits `dismiss` so the host can drop it from the
 * ephemeral queue. Clicking it dismisses immediately. Nothing is persisted.
 */
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import type { RewardFeedbackEvent } from '@/types/gamification';

const props = withDefaults(
  defineProps<{
    /** The reward event this toast represents. */
    event: RewardFeedbackEvent;
    /** Auto-dismiss delay in ms; 0 disables auto-dismiss. */
    duration?: number;
  }>(),
  { duration: 6000 },
);

const emit = defineEmits<{ dismiss: [] }>();

/** Generic upward 5-point star glyph (UI icon — not constellation geometry). */
const STAR_PATH =
  'M12 2.5l2.94 5.96 6.58.96-4.76 4.64 1.12 6.55L12 18.56l-5.88 3.09 1.12-6.55-4.76-4.64 6.58-.96z';

/** Star milestones show a coin; fragment gains show a signed delta. */
const isStarReward = computed(
  () => props.event.kind === 'milestone' || props.event.kind === 'star_born',
);
const fragmentsLabel = computed(() => {
  const delta = props.event.fragments_delta;
  if (delta === undefined || delta === null) {
    return null;
  }
  return `${delta > 0 ? '+' : ''}${delta}`;
});

// Self-expiry, paused on hover/focus so it can be read.
let timer: ReturnType<typeof setTimeout> | null = null;
const clearTimer = (): void => {
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
};
const startTimer = (): void => {
  if (!import.meta.client || props.duration <= 0) {
    return;
  }
  clearTimer();
  timer = setTimeout(() => emit('dismiss'), props.duration);
};

onMounted(startTimer);
onBeforeUnmount(clearTimer);
</script>

<template>
  <div
    class="w-full rounded bg-white p-[2px] shadow-[0_0_3px_rgba(0,0,0,0.1)]"
    role="status"
    aria-live="polite"
    @mouseenter="clearTimer"
    @mouseleave="startTimer"
    @focusin="clearTimer"
    @focusout="startTimer"
  >
    <div
      class="flex cursor-pointer items-center gap-4 rounded-[2px] border border-onboarding-border p-[14px]"
      @click="emit('dismiss')"
    >
      <p class="m-0 min-w-0 flex-1 truncate font-app text-microcopy-strong text-fontColor">
        {{ event.title }}
      </p>

      <!-- Star milestone → gold coin; fragment gain → signed cyan delta. -->
      <span
        v-if="isStarReward"
        class="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-star"
        aria-hidden="true"
      >
        <svg viewBox="0 0 24 24" class="h-3 w-3 fill-white">
          <path :d="STAR_PATH" />
        </svg>
      </span>
      <span
        v-else-if="fragmentsLabel"
        class="shrink-0 font-app text-microcopy-strong text-fragment"
      >
        {{ fragmentsLabel }}
      </span>
    </div>
  </div>
</template>
